import { Form, message } from 'antd';
import React, { ReactNode, memo, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DefaultForm from 'components/composites/DefaultForm';
import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import { INSUFFICIENT_PERMISSIONS_MESSAGE } from 'constants/internal-admin-roles';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';

type BarnOwlDrawerSubmitResult = {
  error?: string;
  successMessage?: string;
};

type Props<T> = {
  drawerValue: string;
  title: string;
  footerLabel: string;
  submit: (values: T) => Promise<BarnOwlDrawerSubmitResult>;
  children: ReactNode;
};

function BackOfficeBarnOwlFormDrawer<T>({
  drawerValue,
  title,
  footerLabel,
  submit,
  children
}: Props<T>): JSX.Element {
  const [form] = Form.useForm<T>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const drawer = searchParams.get(SEARCH_PARAMS.DRAWER.key);
  const visible = drawer === drawerValue;

  const canSubmit = useMyUserHasInternalPermission({
    resource: ProtectedResource.IP_CAMERA,
    action: ResourceAction.ADD
  });

  const handleClose = useCallback(() => {
    form.resetFields();
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
      return newSearchParams;
    });
  }, [form, setSearchParams]);

  const submitFormAndCloseDrawer = () => {
    form
      .validateFields()
      .then(async (values) => {
        setIsSubmitting(true);
        try {
          const result = await submit(values);
          if (result.error) {
            message.error(result.error);
            return;
          }
          if (result.successMessage) {
            message.success(result.successMessage);
          }
          handleClose();
        } finally {
          setIsSubmitting(false);
        }
      })
      .catch(() => undefined);
  };

  if (!canSubmit) {
    return (
      <BackOfficeDrawer
        visible={visible}
        backOfficeSection={title}
        actions={[]}
        noFooter
        handleClose={handleClose}
      >
        {INSUFFICIENT_PERMISSIONS_MESSAGE}
      </BackOfficeDrawer>
    );
  }

  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection={title}
      actions={[]}
      footerLabel={footerLabel}
      handleSave={submitFormAndCloseDrawer}
      isSubmitting={isSubmitting}
      handleClose={handleClose}
    >
      <DefaultForm form={form} layout="vertical">
        {children}
      </DefaultForm>
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeBarnOwlFormDrawer) as typeof BackOfficeBarnOwlFormDrawer;
